import { useCallback } from 'react';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useLastValidOrder } from '../../context/Order';
import { useNavigateToMenu } from '../useNavigateToMenu';

/**
 * Navigates to the menu from the last order.
 * If there is no last order, navigate to the locations screen instead.
 */
export const useNavigateToLastOrderMenu = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { lastValidOrder } = useLastValidOrder();
  const navigateToMenu = useNavigateToMenu();

  // ────────────────────────────────────────────────────────────────────

  const deliveryOrderDetail = lastValidOrder?.deliveryOrderDetail;
  const lastOrderAddressId = deliveryOrderDetail?.address?.id;
  const lastOrderRestaurantSlug = lastValidOrder?.restaurant.slug;

  // ────────────────────────────────────────────────────────────────────

  const handleGoToLocations = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/menu/locations');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'MenuTab',
      params: {
        screen: 'Locations',
      },
    });
  }, [isExpoRouterEnabled, navigation, router]);

  // ────────────────────────────────────────────────────────────────────

  return useCallback(() => {
    // Navigating to last order's delivery menu.
    if (lastOrderAddressId) {
      navigateToMenu({
        orderType: 'delivery',
        deliveryOrderDetail,
      });

      return;
    }

    // Navigating to last order's pickup/outpost menu.
    if (lastOrderRestaurantSlug) {
      navigateToMenu({
        orderType: 'pickup',
        restaurantSlug: lastOrderRestaurantSlug,
      });

      return;
    }

    // Navigating to the locations screen.
    handleGoToLocations();
  }, [
    lastOrderAddressId,
    lastOrderRestaurantSlug,
    deliveryOrderDetail,
    navigateToMenu,
    handleGoToLocations,
  ]);
};
